import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Upload, 
  Image as ImageIcon, 
  Download, 
  Trash2, 
  Eye, 
  Send, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  RotateCcw
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { format } from "date-fns";

interface OrderProofsTabProps {
  orderId?: string;
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
}

interface OrderProof {
  id: string;
  order_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  version: number;
  status: "pending" | "sent" | "approved" | "revision_requested";
  customer_email: string | null;
  approval_token: string | null;
  sent_at: string | null;
  approved_at: string | null;
  revision_notes: string | null;
  created_at: string;
}

const statusConfig = {
  pending: { label: "Odottaa lähetystä", color: "bg-muted text-muted-foreground", icon: Clock },
  sent: { label: "Lähetetty", color: "bg-blue-500/10 text-blue-500", icon: Send },
  approved: { label: "Hyväksytty", color: "bg-green-500/10 text-green-500", icon: CheckCircle },
  revision_requested: { label: "Muutospyyntö", color: "bg-orange-500/10 text-orange-500", icon: AlertCircle },
};

export function OrderProofsTab({ orderId, orderNumber, customerName, customerEmail }: OrderProofsTabProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewProof, setPreviewProof] = useState<{ url: string; name: string } | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState<OrderProof | null>(null);
  const [emailToSend, setEmailToSend] = useState("");

  // Fetch proofs
  const { data: proofs = [], isLoading } = useQuery({
    queryKey: ["order-proofs", orderId],
    queryFn: async () => {
      if (!orderId) return [];
      const { data, error } = await supabase
        .from("order_proofs")
        .select("*")
        .eq("order_id", orderId)
        .order("version", { ascending: false });
      
      if (error) throw error;
      return data as OrderProof[];
    },
    enabled: !!orderId,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!orderId) throw new Error("Order ID puuttuu");

      // Get current max version
      const maxVersion = proofs.length > 0 ? Math.max(...proofs.map(p => p.version)) : 0;

      // Upload to storage
      const filePath = `${orderId}/v${maxVersion + 1}-${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("order-proofs")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Save metadata to database
      const { data: { user } } = await supabase.auth.getUser();
      const { error: dbError } = await supabase
        .from("order_proofs")
        .insert({
          order_id: orderId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          version: maxVersion + 1,
          created_by: user?.id || null,
        });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-proofs", orderId] });
      toast.success("Vedos ladattu onnistuneesti");
    },
    onError: (error: Error) => {
      toast.error("Virhe vedoksen latauksessa", { description: error.message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (proof: OrderProof) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("order-proofs")
        .remove([proof.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("order_proofs")
        .delete()
        .eq("id", proof.id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-proofs", orderId] });
      toast.success("Vedos poistettu");
    },
    onError: (error: Error) => {
      toast.error("Virhe vedoksen poistossa", { description: error.message });
    },
  });

  // Send proof mutation
  const sendProofMutation = useMutation({
    mutationFn: async ({ proofId, email }: { proofId: string; email: string }) => {
      const { data, error } = await supabase.functions.invoke("send-proof", {
        body: {
          proof_id: proofId,
          customer_email: email,
          order_number: orderNumber,
          customer_name: customerName || "Asiakas",
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-proofs", orderId] });
      toast.success("Vedos lähetetty asiakkaalle");
      setSendDialogOpen(false);
      setSelectedProof(null);
      setEmailToSend("");
    },
    onError: (error: Error) => {
      toast.error("Virhe vedoksen lähetyksessä", { description: error.message });
    },
  });

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(file => {
      // Only images allowed for proofs
      if (!file.type.startsWith("image/")) {
        toast.error("Vain kuvat sallittu", { description: `${file.name} ei ole kuva` });
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error("Tiedosto liian suuri", { description: `${file.name} on yli 20MB` });
        return false;
      }
      return true;
    });

    for (const file of validFiles) {
      setUploadingFiles(prev => [...prev, file.name]);
      try {
        await uploadMutation.mutateAsync(file);
      } finally {
        setUploadingFiles(prev => prev.filter(name => name !== file.name));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const downloadProof = async (proof: OrderProof) => {
    try {
      const { data, error } = await supabase.storage
        .from("order-proofs")
        .createSignedUrl(proof.file_path, 3600);

      if (error) throw error;
      
      window.open(data.signedUrl, "_blank");
    } catch (error) {
      toast.error("Virhe vedoksen latauksessa", { description: error instanceof Error ? error.message : "Tuntematon virhe" });
    }
  };

  const previewImage = async (proof: OrderProof) => {
    try {
      const { data, error } = await supabase.storage
        .from("order-proofs")
        .createSignedUrl(proof.file_path, 3600);

      if (error) throw error;
      
      setPreviewProof({ url: data.signedUrl, name: proof.file_name });
    } catch (error) {
      toast.error("Virhe kuvan esikatselussa", { description: error instanceof Error ? error.message : "Tuntematon virhe" });
    }
  };

  const openSendDialog = (proof: OrderProof) => {
    setSelectedProof(proof);
    setEmailToSend(customerEmail || "");
    setSendDialogOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (!orderId) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Tallenna tilaus ensin lisätäksesi vedoksia
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* Upload area */}
      <Card
        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
          accept="image/*"
        />
        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground mb-2">
          Vedä vedoskuva tähän tai klikkaa valitaksesi
        </p>
        <p className="text-xs text-muted-foreground">
          Tuetut tiedostotyypit: JPG, PNG, WEBP (max 20MB)
        </p>
      </Card>

      {/* Uploading files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((fileName) => (
            <Card key={fileName} className="p-3">
              <div className="flex items-center gap-3">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                <span className="text-sm">{fileName}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Proofs list */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan vedoksia...</div>
      ) : proofs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Ei vedoksia. Lataa ensimmäinen vedos yllä.
        </div>
      ) : (
        <div className="space-y-3">
          {proofs.map((proof) => {
            const config = statusConfig[proof.status];
            const StatusIcon = config.icon;
            
            return (
              <Card key={proof.id} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <ImageIcon className="h-10 w-10 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">Versio {proof.version}</span>
                      <Badge variant="secondary" className={config.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{proof.file_name}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{formatFileSize(proof.file_size)}</span>
                      <span>•</span>
                      <span>Luotu: {format(new Date(proof.created_at), "dd.MM.yyyy HH:mm")}</span>
                      {proof.sent_at && (
                        <>
                          <span>•</span>
                          <span>Lähetetty: {format(new Date(proof.sent_at), "dd.MM.yyyy HH:mm")}</span>
                        </>
                      )}
                      {proof.approved_at && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">Hyväksytty: {format(new Date(proof.approved_at), "dd.MM.yyyy HH:mm")}</span>
                        </>
                      )}
                    </div>
                    {proof.customer_email && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Lähetetty: {proof.customer_email}
                      </p>
                    )}
                    {proof.revision_notes && (
                      <div className="mt-2 p-2 bg-orange-500/10 rounded text-sm">
                        <span className="font-medium text-orange-600">Muutospyyntö:</span>
                        <p className="text-muted-foreground mt-1">{proof.revision_notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => previewImage(proof)}
                      title="Esikatsele"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => downloadProof(proof)}
                      title="Lataa"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {proof.status !== "approved" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => openSendDialog(proof)}
                        title={proof.status === "sent" ? "Lähetä uudelleen" : "Lähetä hyväksyttäväksi"}
                      >
                        {proof.status === "revision_requested" ? (
                          <RotateCcw className="h-4 w-4" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    {proof.status === "pending" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMutation.mutate(proof)}
                        disabled={deleteMutation.isPending}
                        title="Poista"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewProof} onOpenChange={() => setPreviewProof(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewProof?.name}</DialogTitle>
          </DialogHeader>
          {previewProof && (
            <div className="mt-4">
              <img
                src={previewProof.url}
                alt={previewProof.name}
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lähetä vedos hyväksyttäväksi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Asiakkaan sähköposti</Label>
              <Input
                id="email"
                type="email"
                value={emailToSend}
                onChange={(e) => setEmailToSend(e.target.value)}
                placeholder="asiakas@esimerkki.fi"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Asiakas saa sähköpostin, jossa on linkki vedoksen hyväksymiseen tai muutospyynnön tekemiseen.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Peruuta
            </Button>
            <Button 
              onClick={() => {
                if (selectedProof && emailToSend) {
                  sendProofMutation.mutate({ 
                    proofId: selectedProof.id, 
                    email: emailToSend 
                  });
                }
              }}
              disabled={!emailToSend || sendProofMutation.isPending}
            >
              {sendProofMutation.isPending ? "Lähetetään..." : "Lähetä"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
